import { clientLog } from './util';

/**
 * Expiry handling for Entra ID identity tokens.
 *
 * The server validates the identity token's lifetime, and rejects an expired
 * one with ReAuth, which sends the client back through sign-in. Under nested
 * app authentication the token comes from the Outlook host's broker rather
 * than from our own MSAL cache, and the broker can hand back an ID token that
 * expired minutes ago. Sending it costs a round trip and a sign-in attempt,
 * so every acquisition path checks expiry here before using a token.
 *
 * Lives outside plugin.tsx so the fallback dialog, which runs in its own
 * webview with its own MSAL instance, applies the same rule.
 */

export interface DecodedJwtExpiration {
    expirationDate: Date;
    expirationTimestamp: number;
    isExpired: boolean;
    timeUntilExpiration: number;
    payload: any;
}

export function decodeJwtExpiration(token: string, now: () => number = Date.now): DecodedJwtExpiration | null {
    try {
        // Validate input
        if (!token || typeof token !== 'string') {
            clientLog('Invalid token: must be a non-empty string');
            return null;
        }

        // Split the JWT into its three parts
        const parts = token.split('.');
        if (parts.length !== 3) {
            clientLog('Invalid JWT format: must have 3 parts separated by dots');
            return null;
        }

        // Get the payload (middle part)
        const payload = parts[1];

        // Base64url decode the payload
        // Base64url is like base64 but with URL-safe characters
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');

        // Add padding if needed
        const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);

        // Decode and parse JSON
        const decodedPayload = JSON.parse(atob(padded));

        // Check if exp claim exists
        if (!decodedPayload.exp) {
            clientLog('Token does not contain an expiration (exp) claim');
            return null;
        }

        // Convert Unix timestamp to JavaScript Date
        const expirationDate = new Date(decodedPayload.exp * 1000);

        // Validate the date
        if (isNaN(expirationDate.getTime())) {
            clientLog('Invalid expiration timestamp');
            return null;
        }

        return {
            expirationDate,
            expirationTimestamp: decodedPayload.exp,
            isExpired: now() > expirationDate.getTime(),
            timeUntilExpiration: expirationDate.getTime() - now(),
            payload: decodedPayload, // Return full payload for reference
        };
    } catch (error) {
        clientLog(`Failed to decode JWT expiration: ${error && error.message}`);
        return null;
    }
}

// Margin matches the server-side clock skew allowance (5 minutes)
export const ID_TOKEN_EXPIRY_MARGIN_MS = 5 * 60 * 1000;

/**
 * True when the token is expired, or close enough to expiring that the round
 * trip to the server could outlive it. Undecodable tokens return false: the
 * server is the authority, and refusing to send something we cannot read
 * would break sign-in on a token shape we did not anticipate.
 */
export function isIdTokenExpiredOrExpiring(token: string, now: () => number = Date.now): boolean {
    const decoded = decodeJwtExpiration(token, now);
    if (!decoded) {
        // Cannot decode; send it and let the server decide
        return false;
    }
    return decoded.timeUntilExpiration < ID_TOKEN_EXPIRY_MARGIN_MS;
}

/**
 * Expiry facts about an identity token, for diagnostics. Never returns the
 * token itself: these reports end up in support tickets and server logs, and
 * an identity token is a credential.
 */
export function describeTokenExpiry(token: string, now: () => number = Date.now) {
    if (!token) {
        return { present: false };
    }
    const decoded = decodeJwtExpiration(token, now);
    if (!decoded) {
        return { present: true, decodable: false };
    }
    return {
        present: true,
        decodable: true,
        expiresAt: decoded.expirationDate.toISOString(),
        isExpired: decoded.isExpired,
        secondsUntilExpiration: Math.round(decoded.timeUntilExpiration / 1000),
        tenantId: decoded.payload && decoded.payload.tid,
    };
}
